import { differenceInDays, format } from "date-fns";
import { observer } from "mobx-react";
import type React from "react";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";

import {
  DEFAULT_DEBOUNCE_TIME,
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
} from "constants/misc-constants";
import { InvitationStatus } from "models/product/StudentInvitation";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useProduct } from "hooks/useProduct";
import { useReactTooltip } from "hooks/useReactTooltip";
import { useLanguage } from "hooks/useSelectedLanguage";

import { Paginator } from "components/PaginatorRemake/Paginator";
import { ProgressBar } from "components/ProgressBar/ProgressBar";
import { Select } from "components/SelectUI/Select";
import { SortableTable } from "components/SortableTable/SortableTable";
import type { TableRow } from "components/SortableTable/SortableTable.model";
import { StatusTag } from "components/StatusTag/StatusTag";

import { CssButton, CssClickableIcon, CssFlex, CssGrid } from "styles/helpers/layout";

import {
  StyledField,
  StyledInput,
  StyledProgressBarContainer,
  StyledStat,
  StyledStatsBox,
} from "./InvitedStudentList.styled";

type InviteQueryParams = {
  page: number;
  resultsPerPage: number;
  email: string | null;
  status: string;
};

export const InvitedStudentList: FC = observer(() => {
  const { currentLanguage } = useLanguage();
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("teacher-dashboard");
  const { getStudentTermByDomain } = useDomainHandler();

  const { loading, invitationList, fetchInvitationList, deleteInviteById } = useProduct();
  useReactTooltip(loading);

  const [filters, setFilters] = useState<InviteQueryParams>({
    email: null,
    status: "all",
    page: DEFAULT_PAGE,
    resultsPerPage: DEFAULT_ROWS_PER_PAGE,
  });

  const { email, status, page, resultsPerPage } = filters;
  const accepted = status === "all" ? undefined : status === InvitationStatus.ACCEPTED;

  // Define debounced callback method to avoid too many calls to endpoint while user is typing in search box
  const refreshSentInviteList = useDebouncedCallback(
    () => fetchInvitationList({ page, resultsPerPage, accepted, email }),
    DEFAULT_DEBOUNCE_TIME,
    { leading: false },
  );

  // Trigger request whenever any of the filter values change
  useEffect(() => {
    refreshSentInviteList();
  }, [page, resultsPerPage, accepted, email]);

  // Delete an invite and refresh list afterwards
  const onDeleteButtonClick = (inviteId: string) => {
    deleteInviteById(inviteId).then(() => {
      refreshSentInviteList();
    });
  };

  // Options for the status selector (will refresh if user changes their UI language)
  const inviteStatusOptions = useMemo(
    () => [
      {
        value: "all",
        label: t("studentLicense.invitationList.filters.status.options.all"),
      },
      {
        value: "accepted",
        label: t("studentLicense.invitationList.filters.status.options.accepted"),
      },
      {
        value: "awaiting",
        label: t("studentLicense.invitationList.filters.status.options.awaiting"),
      },
    ],
    [currentLanguage],
  );

  const numPages = Math.ceil(invitationList.totalInvitations / resultsPerPage);
  const tableData = useMemo<TableRow[]>(
    () =>
      invitationList.invitations.map(({ id, email, status, createdOn }) => {
        const isRemovableInvite =
          status === InvitationStatus.PENDING &&
          differenceInDays(new Date(), new Date(createdOn)) <= 14;

        return {
          id: `invite-${id}-row`,
          cells: [
            {
              id: `invite-${id}-email`,
              content: email,
              dataCy: `invite-${id}-email`,
            },
            {
              id: `invite-${id}-sent-date`,
              dataCy: `invite-${id}-sent-date`,
              content: (
                <span
                  data-tip={
                    isRemovableInvite
                      ? t("studentLicense.invitationList.tooltip.removableInvite")
                      : t("studentLicense.invitationList.tooltip.removalExpired")
                  }
                >
                  {format(new Date(createdOn), "dd-MM-yyyy")}
                </span>
              ),
            },
            {
              id: `invite-${id}-status`,
              dataCy: `invite-${id}-status`,
              content: (
                <StatusTag
                  data-status={status}
                  data-tip={
                    status === InvitationStatus.ACCEPTED
                      ? t("studentLicense.invitationList.tooltip.acceptedInvite")
                      : undefined
                  }
                  icon={status === InvitationStatus.ACCEPTED ? "far fa-check" : "far fa-hourglass"}
                  variant={status === InvitationStatus.ACCEPTED ? "success" : "warning"}
                  label={t(`studentLicense.invitationList.${status}`)}
                />
              ),
            },
            {
              id: `invite-${id}-action-buttons`,
              content: (
                <>
                  {/* Delete feature is only available for pending invites created in the past 14 days */}
                  {isRemovableInvite && (
                    <CssButton
                      data-cy={`delete-invite-${id}-button`}
                      variant="text"
                      disabled={loading}
                      onClick={() => onDeleteButtonClick(id)}
                    >
                      <CssClickableIcon iconName="far fa-trash-alt" />{" "}
                      {tCommon("iconButtonLabel.delete")}
                    </CssButton>
                  )}
                </>
              ),
            },
          ],
        };
      }),
    [invitationList.invitations, loading],
  );

  // Update filters object passing function to "setFilters" to ensure we get the right prev state
  // @see https://stackoverflow.com/a/69945784/419194
  const updateFilterValue = (key: keyof InviteQueryParams, value: string | number | null) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
      // Force user back to first page if any other filter changes, as the current page
      // may no longer exist after applying the filters
      ...(key !== "page" ? { page: 1 } : {}),
    }));
  };

  const {
    totalInvitations,
    totalAcceptedInvitations = 0,
    totalPendingInvitations = 0,
  } = invitationList;

  return (
    <CssFlex data-cy="invited-student-list" flexDirection="column" gap={1}>
      {/* -- Filter area -- */}
      <CssGrid gridTemplateColumns="1fr 15rem" gap={1.5}>
        <StyledField>
          <label htmlFor="invited-student-email-search-input">
            {t("studentLicense.invitationList.filters.studentSearch.label", {
              studentTerm: getStudentTermByDomain(),
            })}
          </label>
          <StyledInput
            data-cy="invite-student-email-search-input"
            id="invited-student-email-search-input"
            value={email ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateFilterValue("email", e.target.value)
            }
          />
        </StyledField>
        <StyledField>
          <label id="invite-status-selector-label" htmlFor="invite-status-selector">
            {t("studentLicense.invitationList.filters.status.label")}
          </label>
          <Select
            id="invite-status-selector"
            aria-labelledby="invite-status-selector-label"
            dataCy="invite-status-selector"
            options={inviteStatusOptions}
            showPlaceholderInOptionList={false}
            value={status}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              updateFilterValue("status", event.target.value);
            }}
          />
        </StyledField>
      </CssGrid>
      {/* -- Global statistics -- */}
      <StyledStatsBox>
        <StyledStat>
          {`${t("invitationManagement.allInviteesList.statistics.accepted")}: `}
          <StatusTag
            data-cy="accepted-invites-count"
            label={`${totalAcceptedInvitations}`}
            variant="success"
          />
        </StyledStat>
        <StyledStat>
          {`${t("invitationManagement.allInviteesList.statistics.pending")}: `}
          <StatusTag
            data-cy="pending-invites-count"
            label={`${totalPendingInvitations}`}
            variant="warning"
          />
        </StyledStat>
        <StyledStat>
          {`${t("invitationManagement.allInviteesList.statistics.total")}: `}
          <StatusTag data-cy="total-sent-invites-count" label={`${totalInvitations}`} />
        </StyledStat>
        <StyledProgressBarContainer>
          <ProgressBar currentValue={totalAcceptedInvitations} maxValue={totalInvitations} />
          <span>{`${totalAcceptedInvitations} / ${totalInvitations}`}</span>
        </StyledProgressBarContainer>
      </StyledStatsBox>
      {/* -- Paginated list -- */}
      <SortableTable
        flexStartCellHeaders
        headerCells={[
          {
            id: "invite-student-email-column",
            title: t("studentLicense.invitationList.columns.email"),
            size: "2fr",
          },
          {
            id: "invite-sent-date-column",
            title: t("studentLicense.invitationList.columns.sentDate"),
            size: "1fr",
          },
          {
            id: "invite-status-column",
            title: t("studentLicense.invitationList.columns.status"),
            size: "1fr",
          },
          {
            id: "invite-actions-column",
            title: t("studentLicense.invitationList.columns.actions"),
            size: "8rem",
          },
        ]}
        rows={tableData}
        variant="white"
      />
      {page && numPages > 1 && (
        <Paginator
          currentPage={+page}
          totalPages={numPages}
          onPageChange={(selectedPage) => updateFilterValue("page", selectedPage)}
        />
      )}
    </CssFlex>
  );
});
